import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@mui/material';
import { TableContainer, TableBody, Intro } from './CookieTable.styled';
import { CookieInfo } from './Cookies';
import CookieDetailBody from './CookieDetailBody';

interface CookieTableProps {
  cookieType: string;
}

const CookieTable: FC<CookieTableProps> = ({ cookieType }) => {
  const { t } = useTranslation();

  const intro = t(`cookies.cookie-statement.content.cookieOverview.${cookieType}.intro`);

  const showIntro = () => {
    if (typeof intro === 'string' && intro.trim().length === 0) {
      return false;
    }
    if (intro === null) {
      return false;
    }

    return true;
  };

  const toCookieInfo = (value: unknown): CookieInfo | null => {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const item = value as Record<string, unknown>;

    if (
      typeof item.name !== 'string' ||
      typeof item.provider !== 'string' ||
      typeof item.purpose !== 'string' ||
      typeof item.duration !== 'string'
    ) {
      return null;
    }

    return {
      name: item.name,
      provider: item.provider,
      purpose: item.purpose,
      duration: item.duration,
    };
  };

  const getCookieInfos = (cookieType: string): CookieInfo[] => {
    const raw = t(
      `cookies.cookie-statement.content.cookieOverview.${cookieType}.cookies`,
      { returnObjects: true, defaultValue: [] }
    ) as unknown;

    const cookieList = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object'
        ? Object.values(raw as Record<string, unknown>)
        : [];

      return cookieList
              .map(toCookieInfo)
              .filter((cookie): cookie is CookieInfo => cookie !== null);
  };

  return (
    <>
      {showIntro() && <Intro variant="body2">{intro}</Intro>}
      <TableContainer>
        <Table data-testid={`cookie-overview-table-${cookieType}`}>
          <TableBody>
            {
              getCookieInfos(cookieType).map((cookieInfo, index, arr) => {
                const isLast = index === arr.length - 1;

                return (
                  <CookieDetailBody
                    key={`${cookieType}-${index}`}
                    cookieType={cookieType}
                    index={index}
                    cookieInfo={cookieInfo}
                    isLast={isLast}
                  />
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CookieTable;
